import React, { useEffect, useState } from "react";
import Maximize from "../../Maximize";
import MultiBarLineChartECharts from "../../BarChart";
import { message, Typography } from "antd";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { TableRecord } from "../../Table";
import axiosInstance from "../../../utils/axiosInstance";
import { CONTRIBUTION_PERC_API } from "../../../constants/API";
import { getTenantId } from "../../../utils/auth";
type SpendEffectByChannelProps = {
  jobId: string;
};
type ContributionItem = {
  Variables: string;
  Contributions: number;
};

const SpendEffectByChannel = ({ jobId }: SpendEffectByChannelProps) => {
  const [historicSpendContributionData, setHistoricSpendContributionData] =
    useState<number[]>([]);
  const [historicSpendContributionLabel, setHistoricSpendContributionLabel] =
    useState<string[]>([]);
  const [rawContributionData, setRawContributionData] = useState<
    Record<string, number>
  >({});
  const [rawEffectData, setRawEffectData] = useState<Map<string, number>>(
    new Map()
  );
  const [effectShareData, setEffectShareData] = useState<number[]>([]);
  const [effectShareLabel, setEffectShareLabel] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    if (
      Object.keys(rawContributionData).length === 0 ||
      rawEffectData.size === 0
    ) {
      return;
    }

    const combined: {
      label: string;
      spend: number;
      effect: number;
    }[] = Object.entries(rawContributionData).map(([label, spend]) => ({
      label,
      spend,
      effect: rawEffectData.get(label) ?? 0,
    }));

    // Sort by effect descending
    combined.sort((a, b) => b.effect - a.effect);

    let displayChannels = combined;
    let otherSpend = 0;
    let otherEffect = 0;

    if (combined.length > 5) {
      displayChannels = combined.slice(0, 5);
      const others = combined.slice(5);
      for (const item of others) {
        otherSpend += item.spend;
        otherEffect += item.effect;
      }
      displayChannels.push({
        label: "Others",
        spend: otherSpend,
        effect: otherEffect,
      });
    }

    setHistoricSpendContributionLabel(
      displayChannels.map(
        (item) => item.label.replace(/spend/i, "").trim() // removes "spend" (case-insensitive) and trims spaces
      )
    );
    setHistoricSpendContributionData(displayChannels.map((item) => item.spend));
    setEffectShareLabel(
      displayChannels.map((item) => item.label.replace(/spend/i, "").trim())
    );
    setEffectShareData(displayChannels.map((item) => item.effect));
  }, [rawContributionData, rawEffectData]);
  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    // Fetch job config
    axiosInstance
      .get(`/api/jobConfig/${jobId}`)
      .then((response) => {
        const mediaVariables =
          response.data.dataMappingConfig.mappings.mmm.columnMapping
            .media_variables;

        axiosInstance
          .post(CONTRIBUTION_PERC_API, {
            spendColumns: mediaVariables,
            jobConfigId: jobId,
            tenantId: getTenantId(),
          })
          .then((contribResponse) => {
            const contributionData: Record<string, number> =
              contribResponse.data.totalContribution || {};
            setRawContributionData(contributionData);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching contribution data:", error);
            setLoading(false);
          });

        // Fetch MMM output data
        axiosInstance
          .get(`/api/mmm/output/${jobId}`)
          .then((outputResponse) => {
            let outputJson = outputResponse.data.result;

            try {
              const adstock = outputJson["adstock"];
              if (adstock && Array.isArray(adstock) && adstock.length > 0) {
                const firstItem = adstock[0];
                let groupedData: Record<string, any> = {};
                if (
                  firstItem.Var_Name &&
                  firstItem.Adstock_Value !== undefined
                ) {
                  groupedData = adstock.reduce((acc: any, item: any) => {
                    const prefix = item.Var_Name;
                    const metric = item.Type;
                    acc[prefix][metric] = item.Adstock_Value;
                    return acc;
                  }, {});
                }
                if (Object.keys(groupedData).length > 0) {
                  const labels = Object.keys(groupedData);
                  const availableMetrics = new Set<string>();
                  for (const label of labels) {
                    for (const metric of Object.keys(groupedData[label])) {
                      {
                        availableMetrics.add(metric);
                      }
                    }
                  }

                  const barDatasets: any[] = [];
                  if (availableMetrics.has("theta")) {
                    barDatasets.push({
                      name: "Adstock",
                      values: labels.map((label) => groupedData[label]?.theta),
                      color: "#73C2FB", // Blue
                    });
                  }

                  if (availableMetrics.has("alpha")) {
                    barDatasets.push({
                      name: "alpha",
                      values: labels.map((label) => groupedData[label]?.alpha),
                      color: "#FF6F61", // Red
                    });
                  }

                  // Add gamma/length if available
                  if (availableMetrics.has("gamma")) {
                    barDatasets.push({
                      name: "gamma",
                      values: labels.map((label) => groupedData[label]?.gamma),
                      color: "#32CD32", // Green
                    });
                  }

                  // If no specific metrics were found, use a generic approach
                  if (barDatasets.length === 0) {
                    // Just use the first numeric property we find
                    const firstLabel = labels[0];
                    const firstMetric = Object.keys(
                      groupedData[firstLabel]
                    ).find(
                      (key) => typeof groupedData[firstLabel][key] === "number"
                    );

                    if (firstMetric) {
                      barDatasets.push({
                        name:
                          firstMetric.charAt(0).toUpperCase() +
                          firstMetric.slice(1),
                        values: labels.map(
                          (label) => groupedData[label]?.[firstMetric]
                        ),
                        color: "#73C2FB", // Blue
                      });
                    }
                  }

                  const chartProps: any = {
                    labels: labels.map((label) =>
                      label
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")
                    ),
                    barDatasets,
                    lineDatasets: [], // No line datasets needed
                  };
                }
              }
            } catch (error) {
              console.error("Error processing adstock data:", error);
              // Use media variables to create fallback data
            }

            // Process VIF factors
            if (outputJson["VifFactors"]) {
              const records: TableRecord[] = [];
              outputJson["VifFactors"]?.forEach(
                (item: { Variable: string; VIF: number }) => {
                  records.push({
                    variable: item.Variable.split("_")
                      ?.map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" "),
                    value: item.VIF,
                  });
                }
              );
            }

            // Process other data

            if (outputJson["Contributions_chart"]) {
              const effectData: ContributionItem[] =
                outputJson?.Contributions_chart?.filter(
                  (item: ContributionItem) =>
                    item.Variables !== "Base" &&
                    item.Variables !== "Seasonality"
                ) || [];
              const totalEffect = effectData.reduce(
                (sum: number, item: ContributionItem) =>
                  sum + Number(item.Contributions),
                0
              );

              // Store contributions as percentages
              const effectMap = new Map<string, number>();
              for (const item of effectData) {
                const percentage =
                  totalEffect > 0
                    ? (Number(item.Contributions) / totalEffect) * 100
                    : 0;
                effectMap.set(
                  item.Variables,
                  parseFloat(percentage.toFixed(2))
                );
              }

              setRawEffectData(effectMap);
            }

            const sortedContributionsChart =
              outputJson.Contributions_chart?.sort((a: any, b: any) => {
                const isAImportant =
                  a.Variables === "Seasonality" || a.Variables === "Base";
                const isBImportant =
                  b.Variables === "Seasonality" || b.Variables === "Base";

                if (isAImportant && isBImportant) {
                  return b.Contributions - a.Contributions;
                }

                if (isAImportant) return -1;
                if (isBImportant) return 1;

                return 0;
              });

            const formattedLabels: string[] = [];
            const formattedData: number[] = [];

            sortedContributionsChart?.forEach((item: any) => {
              const value = Number.parseInt(item.Contributions);
              const formattedValue =
                value >= 100000
                  ? `$${(value / 1000).toFixed(0)}K`
                  : `$${value}`;

              formattedLabels.push(`${item.Variables} (${formattedValue})`);
              formattedData.push(value); // keep numeric if your chart expects numbers
            });

            if (outputJson) {
              const colors = [
                "rgba(75, 192, 192, 1)",
                "rgba(255, 99, 132, 1)",
                "rgba(54, 162, 235, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(153, 102, 255, 1)",
                "rgba(255, 159, 64, 1)",
              ];
              const options = Object.keys(outputJson)
                .filter((key) => key.startsWith("Saturation_"))
                .map((key, idx) => {
                  const data = outputJson[key];
                  if (!data || !Array.isArray(data) || data.length === 0)
                    return null;
                  const datasetName = key
                    .replace("Saturation_", "")
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                    .replace(/_/g, " ");
                  const color = colors[idx % colors.length];
                  const labels = data.map((item: any) => item?.Spends ?? 0);
                  const lineData = data.map((item: any) => {
                    const fieldName = key.replace("Saturation_", "");
                    return item?.[fieldName] ?? 0;
                  });
                  const point = {
                    x: labels[labels.length - 1],
                    y: lineData[lineData.length - 1],
                  };
                  return {
                    key,
                    label: datasetName,
                    chartProps: {
                      labels,
                      dataSets: [
                        {
                          name: datasetName,
                          data: lineData,
                          borderColor: color,
                          backgroundColor: color.replace("1)", "0.3)"),
                        },
                      ],
                      xAxisTitle: "Spends",
                      yAxisTitle: datasetName,
                      point,
                    },
                  };
                })
                .filter(Boolean);
            }
          })
          .catch((error: any) => {
            console.error("Error fetching MMM output data:", error);
            message.error("Failed to fetch analysis data");
          });
      })
      .catch((error: any) => {
        console.error("Error fetching job config:", error);
        message.error("Failed to fetch job configuration");
        setLoading(false);
      });
  }, [jobId]);

  return (
    <div>
      {loading ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <div className="flex flex-col items-center justify-center">
            <ReloadOutlined className="text-4xl text-blue-500 mb-4 animate-spin" />
            <Typography.Text type="secondary">
              Loading spend and effect data...
            </Typography.Text>
          </div>
        </div>
      ) : historicSpendContributionLabel.length > 0 &&
        historicSpendContributionData.length > 0 &&
        effectShareData.length > 0 &&
        effectShareLabel.length > 0 ? (
        <Maximize title="Spend vs. Effect by Channel">
          <div style={{ height: "450px" }}>
            <MultiBarLineChartECharts
              labels={historicSpendContributionLabel} // same as effectShareLabel
              barDatasets={[
                {
                  name: "Spend",
                  values: historicSpendContributionData,
                },
                {
                  name: "Effect",
                  values: effectShareData,
                },
              ]}
              lineDatasets={[]}
            />
            <p className="text-sm text-gray-500 mt-2">
              Spend and Effect are modeled metrics. "Effect" represents the
              estimated impact of each channel. Channels with lower
              contributions are grouped into "Others."
            </p>
          </div>
        </Maximize>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <Typography.Text type="secondary">
            <InfoCircleOutlined className="mr-2" />
            No spend and effect data available. Please check your data source.
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default SpendEffectByChannel;
